import {
  DEFAULT_LADDERS_STYLE,
  LADDERS_PICTURE_STYLES,
  LADDERS_STYLES,
  type LaddersStyle,
} from "@bing/engine-ladders";
import { CLASSIC_BOARD_TONES, LADDERS_BOARD_TONES, type BoardTones } from "./looks";

/* ------------------------------------------------------------------ *
 * مار پله — the LOOKS, data-driven (card-86 round 2, batch W1; design
 * §9.3.13). One entry per look the engine names (`LADDERS_STYLES`):
 *
 *   · a PICTURE look (`jungle`, `bg2`) = its picture + its measurements
 *     JSON (the round-1 schema of `tools/card-art/ladders-scene.cjs`:
 *     `master`, the anchor `board`, the focal `band`, the nullable
 *     `temple`/`templeCut`, `bg`, `edges`) — found by `import.meta.glob`,
 *     the SAME hashed Vite asset import as round 1 (no loading change);
 *   · «اورجینال» (`classic`) = no picture: a plain backdrop from theme
 *     tokens and classic alternating cells.
 *
 * ⚠️ ABSENCE-TOLERANT. A picture look is drawable only when BOTH its
 * measurements and its JPEG (or PNG) fallback are in the tree; its WebP is
 * an optional twin (batch P adds them). So bg2 slots in as
 * `src/assets/ladders/scene-bg2.{jpg,webp}` +
 * `tools/card-art/ladders-scene-bg2-measurements.json` (+ a cut-out named by
 * its `templeCut.file`) with NO code change. A style this build cannot draw
 * — an unknown id, or a look whose files are not here — draws jungle.
 * Jungle keeps its round-1 names (`scene-bg.*`, `ladders-scene-
 * measurements.json`, `temple-base.*`), mapped explicitly below.
 * ------------------------------------------------------------------ */

/** A picture's measurements, in its MASTER's px (`ladders-scene.cjs`; `calm` is retired). */
export interface SceneMeasurements {
  readonly master: { readonly w: number; readonly h: number };
  /** The board's box in the picture: its top-left anchor and its natural width (a square). */
  readonly board: { readonly x: number; readonly y: number; readonly w: number };
  /** The focal band over the board: the spire's tip and x (over the 100 cell), the temple's right. */
  readonly band: {
    readonly top: number;
    readonly spireTip: number;
    readonly spireX: number;
    readonly templeRight: number;
  };
  /** The foreground cut-out's box (drawn OVER the board's corner) — null: the picture has none. */
  readonly temple: {
    readonly x: number;
    readonly y: number;
    readonly w: number;
    readonly h: number;
  } | null;
  readonly bg: { readonly file: string };
  readonly templeCut: { readonly file: string } | null;
  /** The picture's own edge colours: the placeholder under it until it has loaded. */
  readonly edges: {
    readonly top: string;
    readonly bottom: string;
    readonly left: string;
    readonly right: string;
    readonly topStops: readonly string[];
  };
  /** OPTIONAL (round 2): this picture's own cell tones — absent, the jungle's are drawn. */
  readonly cells?: BoardTones;
}

/** A picture's two encodings: the JPEG/PNG fallback (always the `<img src>`) and a WebP twin. */
export interface PictureArt {
  readonly jpg: string;
  readonly webp?: string;
}

export interface PictureLook {
  readonly kind: "picture";
  readonly id: LaddersStyle;
  readonly scene: SceneMeasurements;
  readonly bg: PictureArt;
  /** The cut-out drawn over the board — null when the picture has no foreground. */
  readonly temple: PictureArt | null;
  readonly tones: BoardTones;
  /** The CSS background under the picture: its edge colours (the pre-load placeholder). */
  readonly placeholder: string;
}

export interface PlainLook {
  readonly kind: "plain";
  readonly id: LaddersStyle;
  readonly tones: BoardTones;
}

export type LaddersLook = PictureLook | PlainLook;

/** The last path segment, e.g. `scene-bg.jpg`. */
const baseName = (p: string): string => p.slice(p.lastIndexOf("/") + 1);
/** A file name without its extension. */
const stemOf = (p: string): string => baseName(p).replace(/\.[^.]+$/, "");

function byName<T>(found: Record<string, T>): Readonly<Record<string, T>> {
  const out: Record<string, T> = {};
  for (const [p, v] of Object.entries(found)) out[baseName(p)] = v;
  return out;
}

/** Every picture file of the looks (hashed URLs — or `data:` under Vite's 4 KB inline limit). */
const ART = byName(
  import.meta.glob<string>("../../assets/ladders/*.{jpg,jpeg,png,webp}", {
    eager: true,
    query: "?url",
    import: "default",
  }),
);
/** Every picture look's measurements. */
const MEASURED = byName(
  import.meta.glob<SceneMeasurements>("../../../tools/card-art/ladders-scene*-measurements.json", {
    eager: true,
    import: "default",
  }),
);

/** Where a picture look's files are: jungle keeps its round-1 names, any other look is `<id>`. */
export function pictureFilesOf(id: string): { readonly bg: string; readonly measurements: string } {
  return id === "jungle"
    ? { bg: "scene-bg", measurements: "ladders-scene-measurements.json" }
    : { bg: `scene-${id}`, measurements: `ladders-scene-${id}-measurements.json` };
}

/** The JPEG/PNG + WebP pair named `stem`, or null without the fallback (a WebP alone is not drawn). */
function artOf(stem: string): PictureArt | null {
  const jpg = ART[`${stem}.jpg`] ?? ART[`${stem}.jpeg`] ?? ART[`${stem}.png`];
  if (!jpg) return null;
  const webp = ART[`${stem}.webp`];
  return webp ? { jpg, webp } : { jpg };
}

/**
 * A top stop LIGHTER than this (relative luminance) is never drawn by the placeholder: before the
 * picture arrives it would read as a pale strip at the top of the screen — the operator's «white
 * strip». (The jungle's sunlit sky between its temple and its leaves measures #dfe4d9, ≈ 0.76;
 * every other top stop of the two pictures is under 0.2.)
 */
export const PLACEHOLDER_PALE_LUMA = 0.45;

/** The WCAG relative luminance of a `#rrggbb` colour. */
export function lumaOf(hex: string): number {
  const [r, g, b] = [1, 3, 5].map((i) => {
    const c = parseInt(hex.slice(i, i + 2), 16) / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * (r ?? 0) + 0.7152 * (g ?? 0) + 0.0722 * (b ?? 0);
}

/** The channel-wise mean of two `#rrggbb` colours. */
const mixHex = (a: string, b: string): string =>
  `#${[1, 3, 5]
    .map((i) =>
      Math.round((parseInt(a.slice(i, i + 2), 16) + parseInt(b.slice(i, i + 2), 16)) / 2)
        .toString(16)
        .padStart(2, "0"),
    )
    .join("")}`;

/**
 * The placeholder's top stops: the picture's own, but every PALE one drawn as the picture's darker
 * tones beside it — the mean of the nearest darker stop on each side (the jungle's sky becomes its
 * temple's and its leaves' tone), or the left edge's colour if the picture has no darker top stop.
 * A picture with no pale top stop (bg2) keeps its stops exactly as measured.
 */
export function placeholderTopStops(e: SceneMeasurements["edges"]): string[] {
  const pale = (c: string): boolean => lumaOf(c) > PLACEHOLDER_PALE_LUMA;
  return e.topStops.map((c, i) => {
    if (!pale(c)) return c;
    const before = e.topStops
      .slice(0, i)
      .reverse()
      .find((x) => !pale(x));
    const after = e.topStops.slice(i + 1).find((x) => !pale(x));
    return before && after ? mixHex(before, after) : (before ?? after ?? e.left);
  });
}

/** Round 1's base: the picture's own edge colours, under the parts it does not cover yet — never a
 *  pale top ({@link placeholderTopStops}). */
export function placeholderOf(e: SceneMeasurements["edges"]): string {
  return `linear-gradient(to bottom, rgba(0,0,0,0) 0%, ${e.left} 28%, ${e.bottom} 100%), linear-gradient(to right, ${placeholderTopStops(e).join(", ")})`;
}

function pictureLook(id: LaddersStyle): PictureLook | null {
  const files = pictureFilesOf(id);
  const scene = MEASURED[files.measurements];
  const bg = artOf(files.bg);
  if (!scene || !bg) return null;
  const temple = scene.temple && scene.templeCut ? artOf(stemOf(scene.templeCut.file)) : null;
  return {
    kind: "picture",
    id,
    scene,
    bg,
    temple,
    tones: scene.cells ?? LADDERS_BOARD_TONES,
    placeholder: placeholderOf(scene.edges),
  };
}

/** Every look this build can DRAW, by id (a picture look without its files is absent). */
export const LADDERS_LOOKS: Readonly<Partial<Record<LaddersStyle, LaddersLook>>> = (() => {
  const out: Partial<Record<LaddersStyle, LaddersLook>> = {};
  for (const id of LADDERS_STYLES) {
    if (LADDERS_PICTURE_STYLES.includes(id)) {
      const look = pictureLook(id);
      if (look) out[id] = look;
    } else if (id === "classic") {
      out[id] = { kind: "plain", id, tones: CLASSIC_BOARD_TONES };
    }
  }
  return out;
})();

const FALLBACK = LADDERS_LOOKS[DEFAULT_LADDERS_STYLE] ?? LADDERS_LOOKS.jungle;
if (!FALLBACK) throw new Error("مار پله: the jungle look's picture or measurements are missing");
const JUNGLE: LaddersLook = FALLBACK;

/** The look to draw for `style` — an unknown id, or one this build cannot draw, draws jungle. */
export function lookFor(style: unknown): LaddersLook {
  return typeof style === "string" && Object.prototype.hasOwnProperty.call(LADDERS_LOOKS, style)
    ? (LADDERS_LOOKS[style as LaddersStyle] ?? JUNGLE)
    : JUNGLE;
}

/**
 * The picture files in the tree, by name — for the test that keeps a drop-in from half-landing
 * (a measurements file without its picture, a picture no look names).
 */
export const LADDERS_SCENE_FILES: {
  readonly art: readonly string[];
  readonly measurements: readonly string[];
} = { art: Object.keys(ART).sort(), measurements: Object.keys(MEASURED).sort() };
